import React, { useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography, Link, CircularProgress } from '@weave-mui/material';
import Modal from '@weave-mui/modal';
import Button, { buttonVariants } from '@weave-mui/button';
import { typographyVariants } from '@weave-mui/typography';
import { FileDocumentM as FileDocumentIcon } from '@weave-mui/icons-weave';

import downloadFile from 'utils/downloadFile';
import { addNewNotification } from 'store/actions/notification_actions';

const DEFAULT_INSTRUCTIONS =
  'To upload multiple test cases at a time please use the provided CSV template. At the moment Testing Hub only supports CSV files with up to 50 test cases. Additional test cases will be ignored.';

/**
 * BulkUploadModal - Modal for bulk uploading test cases via CSV file.
 * Matches Figma: instructions, download template link, drag-and-drop zone, Cancel/Submit actions.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback with payload array; should return a Promise
 * @param {string[]} props.fields - Column names for CSV template
 * @param {string} [props.instructions=''] - Optional instruction text (defaults to CSV/50 test cases message)
 */
const BulkUploadModal = ({ onClose, onSubmit, fields, instructions = '' }) => {
  const dispatch = useDispatch();
  const [dragOver, setDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [csvFile, setCSVFile] = useState(null);
  const fileInputRef = useRef(null);

  const instructionText = instructions || DEFAULT_INSTRUCTIONS;

  const handleDragDropClick = useCallback(() => {
    const input = fileInputRef.current;
    if (input) input.click();
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i].kind === 'file') {
          setCSVFile(e.dataTransfer.items[i].getAsFile());
          break;
        }
      }
    }
  }, []);

  const handleDownloadTemplateClick = useCallback(
    (e) => {
      e.preventDefault();
      downloadCSVFile([...fields, 'name'], 'template.csv');
    },
    [fields],
  );

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) setCSVFile(file);
  }, []);

  const resolveColumnName = useCallback((name) => {
    if (name?.includes('vatnumber')) return 'vatnumber';
    return name;
  }, []);

  const processCSVFile = useCallback(
    (csv) => {
      const payload = [];
      const rows = csv.split('\n');
      const columns = rows[0].split(',');

      for (let index = 1; index < rows.length; index++) {
        const row = rows[index];
        if (!row) continue;

        const cells = row.split(',');
        const testcase = {};

        cells.forEach((cell, cellIndex) => {
          const cellName = resolveColumnName(columns[cellIndex]);
          if (!cellName) return;

          let cellValue = cell.trim();

          if (
            cellName === 'testdata' ||
            cellName.includes('skulist') ||
            cellName.includes('priceid')
          ) {
            cellValue = [];
            cell
              .trim()
              .split('|')
              .forEach((keyValuePair) => {
                const [key, value] = keyValuePair.split('=');
                if (key) {
                  cellValue.push([key.trim(), value ? value.trim() : 1]);
                }
              });
          } else if (cellName === 'parameters') {
            cellValue = {};
            cell
              .trim()
              .split('|')
              .forEach((keyValuePair) => {
                const [key, value] = keyValuePair.split('=');
                if (key) {
                  cellValue[key.trim()] = value ? value.trim() : 1;
                }
              });
          }

          testcase[cellName] = cellValue;
        });

        testcase.testdata = {};
        payload.push(testcase);
      }

      onSubmit(payload)
        .then((response) => {
          if (response?.data?.message) {
            dispatch(addNewNotification(response.data.message, 'success'));
          }
        })
        .catch((response) => {
          dispatch(
            addNewNotification(response?.message || 'Upload failed', 'danger'),
          );
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [onSubmit, dispatch, resolveColumnName],
  );

  const readFile = useCallback(
    (file) => {
      if (!file) return;
      setIsProcessing(true);
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        processCSVFile(reader.result);
      };
    },
    [processCSVFile],
  );

  const handleSubmit = useCallback(() => {
    if (csvFile) readFile(csvFile);
  }, [csvFile, readFile]);

  const downloadCSVFile = (rows, filename = 'file.csv') => {
    if (rows.length) {
      const csvContent = 'data:text/csv;charset=utf-8,' + rows.join(',');
      downloadFile.fromContent(filename, encodeURI(csvContent));
    }
  };

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
      <Button
        variant={buttonVariants.OUTLINED}
        onClick={onClose}
        disabled={isProcessing}
      >
        Cancel
      </Button>
      <Button
        variant={buttonVariants.CONTAINED}
        onClick={handleSubmit}
        disabled={!csvFile || isProcessing}
      >
        {isProcessing ? 'Processing...' : 'Submit'}
      </Button>
    </Box>
  );

  const dropZoneSx = {
    border: '1px dashed',
    borderColor: dragOver ? 'primary.main' : 'grey.300',
    borderRadius: 1,
    backgroundColor: dragOver ? 'action.hover' : 'grey.50',
    padding: 4,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: isProcessing ? 'wait' : 'pointer',
    minHeight: 160,
    transition: 'border-color 0.2s, background-color 0.2s',
  };

  const renderDropZoneContent = () => {
    if (isProcessing) {
      return (
        <>
          <CircularProgress size="M" sx={{ mb: 2 }} />
          <Typography variant={typographyVariants.BODY} color="text.secondary">
            Processing file
          </Typography>
        </>
      );
    }
    if (csvFile) {
      return (
        <Typography variant={typographyVariants.BODY} color="text.primary">
          <Typography component="span" sx={{ fontWeight: 600 }}>
            Filename:{' '}
          </Typography>
          {csvFile.name}
        </Typography>
      );
    }
    return (
      <>
        <FileDocumentIcon
          sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }}
          aria-hidden
        />
        <Typography
          variant={typographyVariants.BODY}
          color="text.secondary"
          sx={{ textAlign: 'center' }}
        >
          Choose a file or drag it here
        </Typography>
      </>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      header="Upload test cases"
      actions={actionButtons}
      sx={{
        '& .MuiModalWeave-box': {
          width: '560px',
          maxWidth: '90vw',
        },
        '& .MuiModalWeave-actions': {
          padding: '16px 32px',
        },
        '& .MuiModalWeave-child.MuiBox-root': {
          padding: '24px 32px',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography
          variant={typographyVariants.BODY}
          color="text.primary"
          sx={{ mb: 0 }}
        >
          {instructionText}
        </Typography>

        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="primary"
          onClick={handleDownloadTemplateClick}
          sx={{ alignSelf: 'flex-start', cursor: 'pointer' }}
        >
          Download CSV template
        </Link>

        <Box
          role="button"
          tabIndex={0}
          sx={dropZoneSx}
          onClick={!isProcessing ? handleDragDropClick : undefined}
          onKeyDown={(e) => {
            if (!isProcessing && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleDragDropClick();
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-label="Drop CSV file or click to choose"
        >
          {renderDropZoneContent()}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }}
          aria-hidden
        />
      </Box>
    </Modal>
  );
};

export default BulkUploadModal;
